import React, { useState } from 'react';
import { Container, Typography, Box, Alert, LinearProgress } from '@mui/material';
import SearchBar from '../../../components/search/search-bar/search-bar';
import { checkDuplicateSearch, addSearchToHistory } from '../../../services/searchHistoryService';
import { addLead } from '../../../services/leadService';
import { updateBusinessWithLead } from '../../../services/businessService';
import { Note, LeadAnalyzerVersion } from '../../../models/Lead';
import { API_ENDPOINTS } from '../../../config/api';
import './search-component.scss';

interface SearchResult {
    name: string;
    address?: string;
    place_id: string;  // Changed to match Google Places API field name
    hasWebsite?: boolean;
    website?: string;
    phone?: string;
    city?: string;
    state?: string;
}

interface WebsiteAnalysis {
    is_outdated: boolean;
    score: number;
    visual_score: number;
    detailed_results?: string[];
    issues?: string[];
}

const SearchComponent: React.FC = () => {
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDuplicate, setIsDuplicate] = useState(false);

    // Analysis progress states
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [analysisTotal, setAnalysisTotal] = useState(0);
    const [leadsAdded, setLeadsAdded] = useState(0);
    const [skipped, setSkipped] = useState(0);

    const analyzeWebsite = async (website: string, place_id: string): Promise<WebsiteAnalysis> => {
        const response = await fetch(API_ENDPOINTS.ANALYZE_WEBSITE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                url: website,
                place_id: place_id
            }),
        });

        if (!response.ok) {
            throw new Error('Website analysis failed');
        }

        return response.json();
    };

    const shouldCreateLead = (analysis: WebsiteAnalysis): boolean => {
        return (
            analysis.is_outdated ||
            analysis.score < 60 ||
            analysis.visual_score < -20
        );
    };

    const extractLocationFromAddress = (address: string | undefined): { city: string | undefined, state: string | undefined } => {
        if (!address) return { city: undefined, state: undefined };

        // Example address format: "1816 E 6th St, Austin, TX 78702, USA"
        const parts = address.split(',').map(part => part.trim());
        if (parts.length >= 3) {
            const city = parts[parts.length - 3];
            const stateZip = parts[parts.length - 2].split(' ')[0]; // Get just the state part
            return { city, state: stateZip };
        }
        return { city: undefined, state: undefined };
    };

    const createLead = async (result: SearchResult, industryType: string) => {
        try {
            // Extract city and state from address
            const { city, state } = extractLocationFromAddress(result.address);

            const leadPayload = {
                company: result.name,
                phone: result.phone,
                website: result.website,
                businessId: result.place_id,
                city: city || '',  // Use empty string instead of undefined
                state: state || '', // Use empty string instead of undefined
                industryType: industryType || '', // Use empty string instead of undefined
                status: 'new' as const,
                source: 'search' as const,
                analyzerModel: 'V1' as LeadAnalyzerVersion,
                notes: [{
                    id: Date.now().toString(),
                    text: 'Auto-generated from website analysis.',
                    createdAt: new Date()
                }]
            };

            console.log('Final lead payload:', leadPayload);
            const leadId = await addLead(leadPayload);

            // Update the business with the lead information
            await updateBusinessWithLead(result.place_id, leadId);

            setLeadsAdded(prev => prev + 1);
            console.log('Lead successfully created and business updated');
        } catch (error) {
            console.error('Error creating lead:', error);
            throw error;
        }
    };

    const analyzeBusiness = async (business: SearchResult, industryType: string) => {
        try {
            // Extract city and state from address
            const { city, state } = extractLocationFromAddress(business.address);

            console.group(`Analyzing Business: ${business.name}`);
            console.log('Business Details:', {
                name: business.name,
                address: business.address,
                hasWebsite: business.hasWebsite,
                website: business.website,
                place_id: business.place_id,
                phone: business.phone,
                city,
                state,
                industryType
            });

            // Update the business object with extracted location
            business.city = city || '';
            business.state = state || '';

            if (!business.place_id) {
                console.error('❌ No place_id found for business');
                setSkipped(prev => prev + 1);
                console.groupEnd();
                return;
            }

            // Set hasWebsite based on website presence
            business.hasWebsite = Boolean(business.website);

            if (!business.hasWebsite || !business.website) {
                console.log('📝 No website found - creating lead immediately');
                await createLead(business, industryType);
                console.groupEnd();
                return;
            }

            // Analyze website if present
            console.log('🔍 Starting website analysis...');
            const analysis = await analyzeWebsite(business.website, business.place_id);

            console.log('📊 Website Analysis Results:', {
                is_outdated: analysis.is_outdated,
                score: analysis.score,
                visual_score: analysis.visual_score
            });

            // Check if copyright is more than 4 years old
            const currentYear = new Date().getFullYear();
            const copyrightYear = analysis.detailed_results?.find(result =>
                result.toLowerCase().includes('copyright') && /\d{4}/.test(result)
            )?.match(/\d{4}/)?.[0];

            const isOutdatedCopyright = copyrightYear && (currentYear - parseInt(copyrightYear) > 4);

            if (isOutdatedCopyright) {
                console.log('📅 Outdated copyright detected:', copyrightYear);
                analysis.is_outdated = true;
                analysis.issues = [...(analysis.issues || []), `Copyright year ${copyrightYear} is more than 4 years old`];
            }

            console.log('Analysis Breakdown:', {
                'Website Outdated': analysis.is_outdated ? '❌ Yes' : '✅ No',
                'Overall Score': `${analysis.score}/100 ${analysis.score < 60 ? '❌' : '✅'}`,
                'Visual Score': `${analysis.visual_score} ${analysis.visual_score < -20 ? '❌' : '✅'}`,
                'Copyright Year': copyrightYear ? `${copyrightYear} ${isOutdatedCopyright ? '❌' : '✅'}` : 'Not found'
            });

            if (shouldCreateLead(analysis) || isOutdatedCopyright) {
                console.log('✅ Analysis indicates lead should be created', {
                    reason: isOutdatedCopyright ? 'Outdated copyright' :
                        analysis.is_outdated ? 'Website is outdated' :
                            analysis.score < 60 ? 'Low overall score' :
                                analysis.visual_score < -20 ? 'Poor visual score' : 'Multiple issues'
                });
                await createLead(business, industryType);
            } else {
                console.log('❌ Analysis indicates business should be skipped', {
                    reason: 'Website meets quality standards'
                });
                setSkipped(prev => prev + 1);
            }
            console.groupEnd();
        } catch (error) {
            console.error('❌ Error analyzing business:', error);
            setSkipped(prev => prev + 1);
            console.groupEnd();
        }
    };

    const handleSearch = async (industry: string, location: string) => {
        setLoading(true);
        setError(null);
        setResults([]);
        setIsDuplicate(false);

        // Reset analysis states
        setAnalyzing(false);
        setAnalysisProgress(0);
        setAnalysisTotal(0);
        setLeadsAdded(0);
        setSkipped(0);

        try {
            console.log('Starting search for:', { industry, location });

            // Check for duplicate search using Firestore directly
            const isDuplicateSearch = await checkDuplicateSearch(industry, location);
            if (isDuplicateSearch) {
                console.log('Duplicate search detected');
                setIsDuplicate(true);
                setLoading(false);
                return;
            }

            const query = `${industry} in ${location}`;
            console.log('Sending search query:', query);

            // Make the search request to the backend
            const response = await fetch(API_ENDPOINTS.SEARCH, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
                throw new Error(errorData.error || `Search failed with status ${response.status}`);
            }

            const data = await response.json();

            // Transform the businesses to include hasWebsite
            const businesses: SearchResult[] = (data.businesses || []).map((business: any) => ({
                ...business,
                hasWebsite: Boolean(business.website)
            }));

            console.log('Received businesses:', businesses);
            setResults(businesses);

            if (businesses.length > 0) {
                // Start analysis process
                setAnalyzing(true);
                setAnalysisTotal(businesses.length);
                console.log(`Starting analysis of ${businesses.length} businesses`);

                // Analyze each business
                for (let i = 0; i < businesses.length; i++) {
                    console.log(`Analyzing business ${i + 1} of ${businesses.length}`);
                    await analyzeBusiness(businesses[i], industry);
                    setAnalysisProgress(i + 1);
                }

                // Add to search history after analysis
                console.log('Adding search to history');
                const searchResults = businesses.map((business: SearchResult) => ({
                    id: business.place_id,
                    name: business.name,
                    address: business.address || '',
                    phone: business.phone,
                    website: business.website,
                    hasLead: false,  // Will be updated when lead is created
                    searchHistoryId: '',  // Will be set by the service
                    industryType: industry
                }));

                const historyId = await addSearchToHistory(industry, location, searchResults);
                console.log('Search history updated with ID:', historyId);
            }

            setAnalyzing(false);
        } catch (err: any) {
            console.error('Search error:', err);
            setError(err.message || 'Search failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" className="search-container">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Find Businesses
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Search for businesses by industry and location to find potential clients.
                </Typography>

                <SearchBar onSearch={handleSearch} isLoading={loading} />

                {isDuplicate && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        You've already searched this combination.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {analyzing && (
                    <Box sx={{ my: 4 }}>
                        <LinearProgress variant="determinate" value={(analysisProgress / analysisTotal) * 100} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Analyzing {analysisProgress} of {analysisTotal} businesses...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Leads added: {leadsAdded} | Skipped: {skipped}
                        </Typography>
                    </Box>
                )}

                {results.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        {results.map((result) => (
                            <Box key={result.place_id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="h6">{result.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {result.address}
                                </Typography>
                                {result.hasWebsite && result.website && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <a
                                            href={result.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600"
                                        >
                                            Visit Website
                                        </a>
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default SearchComponent;
